import { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft, ChevronRight, Bed, Bath, Ruler, Camera } from "lucide-react";

interface IDXCarouselProps {
  savedLinkId?: string;
  useFeatured?: boolean;
  viewAllUrl: string;
}

interface IDXListing {
  listingID?: string;
  address?: string;
  cityName?: string;
  state?: string;
  zipcode?: string;
  bedrooms?: string;
  fullBaths?: string;
  partialBaths?: string;
  sqFt?: string;
  fullDetailsURL?: string;
  idxStatus?: string;
  priceData?: {
    value?: {
      formatted?: string;
      raw?: number;
    };
  };
  image?: {
    [key: string]: { url?: string } | number;
    totalCount?: number;
  };
}

const getImageUrl = (listing: IDXListing): string | null => {
  if (listing.image && listing.image["0"] && typeof listing.image["0"] === "object") {
    return (listing.image["0"] as { url?: string }).url || null;
  }
  return null;
};

const getPhotoCount = (listing: IDXListing): number => {
  if (listing.image && typeof listing.image.totalCount === "number") {
    return listing.image.totalCount;
  }
  return 0;
};

const getStatusColor = (status?: string) => {
  const s = (status || "").toUpperCase();
  if (s === "ACTIVE") return "bg-green-600 text-white";
  if (s.includes("COMING")) return "bg-blue-600 text-white";
  return "bg-muted text-muted-foreground";
};

const IDXCarousel = ({ savedLinkId, useFeatured, viewAllUrl }: IDXCarouselProps) => {
  const [listings, setListings] = useState<IDXListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: false,
    slidesToScroll: 1,
    containScroll: "trimSnaps",
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  const fetchListings = async () => {
    setLoading(true);
    setError(null);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const fnName = useFeatured ? "idx-featured" : "idx-savedlink-results";
      const qs = useFeatured ? "" : `?savedLinkId=${savedLinkId}`;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/${fnName}${qs}`,
        {
          headers: {
            apikey: anonKey,
            Authorization: `Bearer ${anonKey}`,
          },
        }
      );

      if (!res.ok) throw new Error(`Failed to fetch (${res.status})`);

      const json = await res.json();
      const arr: IDXListing[] = [];
      if (json && typeof json === "object") {
        for (const key of Object.keys(json)) {
          if (/^\d+$/.test(key) && typeof json[key] === "object") {
            arr.push(json[key]);
          }
        }
      }
      setListings(arr);
    } catch (err: unknown) {
      console.error("IDXCarousel fetch error:", err);
      setError(err instanceof Error ? err.message : "Failed to load listings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
  }, [savedLinkId, useFeatured]);

  if (loading) {
    return (
      <div className="flex gap-4 overflow-hidden">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="min-w-0 shrink-0 basis-full sm:basis-1/2 lg:basis-1/4">
            <Card className="overflow-hidden">
              <Skeleton className="h-48 w-full" />
              <CardContent className="pt-4 space-y-2">
                <Skeleton className="h-6 w-24" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </CardContent>
            </Card>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p className="mb-2">{error}</p>
        <button onClick={fetchListings} className="text-sm underline" style={{ color: "#D5153C" }}>
          Try Again
        </button>
      </div>
    );
  }

  if (listings.length === 0) {
    return <p className="text-center py-8 text-muted-foreground">No listings available.</p>;
  }

  return (
    <div>
      {/* Carousel */}
      <div className="relative">
        {/* Prev button */}
        {canScrollPrev && (
          <button
            onClick={() => emblaApi?.scrollPrev()}
            className="absolute -left-3 md:-left-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
            style={{ color: "#D5153C" }}
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}

        {/* Next button */}
        {canScrollNext && (
          <button
            onClick={() => emblaApi?.scrollNext()}
            className="absolute -right-3 md:-right-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
            style={{ color: "#D5153C" }}
            aria-label="Next slide"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}

        <div ref={emblaRef} className="overflow-hidden">
          <div className="flex -ml-4">
            {listings.map((listing, index) => {
              const imageUrl = getImageUrl(listing);
              const photoCount = getPhotoCount(listing);

              return (
                <div
                  key={listing.listingID || index}
                  className="min-w-0 shrink-0 grow-0 basis-full sm:basis-1/2 lg:basis-1/4 pl-4"
                >
                  <a
                    href={listing.fullDetailsURL || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-200 group h-full">
                      {/* Photo */}
                      <div className="relative h-48 bg-muted overflow-hidden">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={`${listing.address}, ${listing.cityName}`}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                            No Photo
                          </div>
                        )}

                        {/* Status badge */}
                        {listing.idxStatus && (
                          <Badge
                            className={`absolute top-2 left-2 ${getStatusColor(listing.idxStatus)} border-0 text-xs font-semibold`}
                          >
                            {listing.idxStatus}
                          </Badge>
                        )}

                        {/* Photo count dots */}
                        {photoCount > 1 && (
                          <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded-md flex items-center gap-1">
                            <Camera className="h-3 w-3" />
                            {photoCount}
                          </div>
                        )}
                      </div>

                      <CardContent className="pt-3 pb-4 space-y-1.5">
                        {/* Price */}
                        <p className="text-xl font-bold" style={{ color: "#D5153C" }}>
                          {listing.priceData?.value?.formatted || "Price N/A"}
                        </p>

                        {/* Address */}
                        <p className="text-sm text-foreground font-medium leading-snug truncate">
                          {listing.address}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {listing.cityName}, {listing.state} {listing.zipcode}
                        </p>

                        {/* Specs */}
                        <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
                          {listing.bedrooms && (
                            <span className="flex items-center gap-1">
                              <Bed className="h-3.5 w-3.5" /> {listing.bedrooms}
                            </span>
                          )}
                          {listing.fullBaths && (
                            <span className="flex items-center gap-1">
                              <Bath className="h-3.5 w-3.5" /> {listing.fullBaths}
                            </span>
                          )}
                          {listing.sqFt && (
                            <span className="flex items-center gap-1">
                              <Ruler className="h-3.5 w-3.5" /> {Number(listing.sqFt.replace(/,/g, '')).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* MLS Compliance Footer */}
      <div className="text-xs text-gray-400 mt-6 pt-4 border-t border-gray-100 flex flex-col items-center gap-2 text-center">
        <img
          src="https://s3.amazonaws.com/staticos.idxbroker.com/mls-logos/b004-logoURL2-idxsandbox"
          alt="Bright MLS Logo"
          height={24}
          style={{ height: 24 }}
          className="object-contain"
        />
        <p>
          © 2026 Bright MLS. All rights reserved. Information Deemed Reliable, but Not Guaranteed. Listing information is from various brokers who participate in IDX and not all listings may be visible on the site. The property information being provided is for consumers personal, non-commercial use and may not be used for any purpose other than to identify prospective properties consumers may be interested in purchasing. Data services provided by IDX Broker.
        </p>
      </div>
    </div>
  );
};

export default IDXCarousel;
